import styled from 'styled-components';
import React, {
    SyntheticEvent,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import {ImageWrapper} from 'shared/components/ImageLoader';
import Icon from 'shared/Icon';

const RETRY_LIMIT = 6;
const RETRY_INTERVAL = 15000;

export const ProductBackgroundImage = ({
    backgroundImage,
    src,
    alt = '',
    transform = 0,
    style,
    onLoad,
    fallbackSrc,
    resize = false,
    isHardware = false,
    cachingInProgress = false,
    placeholderImg,
}: {
    src: string;
    transform?: number;
    onLoad?: () => void;
    fallbackSrc?: string;
    alt?: string;
    backgroundImage?: string;
    style?: React.CSSProperties;
    resize?: boolean;
    isHardware?: boolean;
    cachingInProgress?: boolean;
    placeholderImg?: string;
}) => {
    const [show, setShow] = useState(false);
    const [generatingText, setGeneratingText] = useState('Image is generating');
    const [showSpinner, setShowSpinner] = useState(true);
    const [imageStyle, setImageStyle] = useState<React.CSSProperties>({
        width: '100%',
        height: '100%',
        ...style,
    });
    const containerRef = useRef<HTMLDivElement>();
    const imageRef = useRef<HTMLImageElement>();
    const fallbackLoad = useRef(false);
    const [imageSource, setImageSource] = useState('');
    const [previewGenerating, setPreviewGenerating] =
        useState(cachingInProgress);

    const resizeImage = useCallback((image: HTMLImageElement) => {
        if (containerRef.current) {
            const containerDimensions =
                containerRef.current.getBoundingClientRect();

            if (image) {
                const width = image.naturalWidth;
                const height = image.naturalHeight;
                const maxHeight = containerDimensions.height;
                const maxWidth = containerDimensions.width;

                if (width > height) {
                    setImageStyle((style) => ({
                        ...style,
                        maxWidth: `${maxWidth}px`,
                    }));
                } else if (width < height) {
                    setImageStyle((style) => ({
                        ...style,
                        maxHeight: `${maxHeight}px`,
                    }));
                } else {
                    setImageStyle((style) => ({
                        ...style,
                        maxWidth: `${maxWidth}px`,
                        maxHeight: `${maxHeight}px`,
                    }));
                }

                setShow(true);
            }
        }
    }, []);

    const imageLoadHandler = useCallback(
        (event: SyntheticEvent<HTMLImageElement>) => {
            const image = event.currentTarget;
            resizeImage(image);
            if (typeof onLoad === 'function') {
                onLoad();
            }
            if (previewGenerating) {
                // show the image if it's loaded successfully after retries
                setPreviewGenerating(false);
            }
        },
        [onLoad, previewGenerating]
    );

    const imageLoadErrorHandler = useCallback(() => {
        if (previewGenerating && !!imageSource) {
            // this will only handle cached images being generated in the backend
            // image source should be the actual cached path so it will be thrown in this handler
            const [baseUrl, params] = imageSource.split('?');
            const queryParams = new URLSearchParams(params);
            const retryCount = parseInt(queryParams.get('retry') ?? '0');
            if (retryCount < RETRY_LIMIT) {
                // this will refetch the image after some retries
                queryParams.set('retry', `${retryCount + 1}`);
                setTimeout(() => {
                    setImageSource(`${baseUrl}?${queryParams.toString()}`);
                }, RETRY_INTERVAL);
            } else if (retryCount >= RETRY_LIMIT) {
                setGeneratingText('Generating image, please refresh page');
                setShowSpinner(false);
            }
        }
        if (imageRef.current && !fallbackLoad.current && fallbackSrc) {
            fallbackLoad.current = true;
            imageRef.current.src = fallbackSrc.startsWith('/')
                ? fallbackSrc
                : `/${fallbackSrc}`;
        } else {
            setShow(false);
        }
    }, [fallbackSrc, previewGenerating, imageSource]);

    const resizeHandler = useCallback(() => {
        if (show) {
            resizeImage(imageRef.current);
        }
    }, [show]);

    useEffect(() => {
        let imgSrc = '';
        if (src && src != '' && src != null && src != 'null') {
            imgSrc = src.startsWith('/') ? src : `/${src}`;
        }

        if (fallbackSrc && imgSrc === '') {
            imgSrc = fallbackSrc.startsWith('/')
                ? fallbackSrc
                : `/${fallbackSrc}`;
        }
        setImageSource(imgSrc);
    }, [src, fallbackSrc]);

    useEffect(() => {
        if (imageSource && !imageSource.includes(src)) {
            setGeneratingText('Image is generating');
            setShowSpinner(true);
            setPreviewGenerating(cachingInProgress);
        }
    }, [src, imageSource, cachingInProgress]);

    useLayoutEffect(() => {
        if (resize) {
            const container = document.querySelector('.mainContent');

            if (container) {
                const observer = new ResizeObserver(resizeHandler);

                observer.observe(container);

                return () => observer.unobserve(container);
            }
        }
    }, [resize, resizeHandler]);

    return (
        <OuterContainer ref={containerRef}>
            <ImageContainer
                $isHardware={isHardware}
                $show={show}
                $backgroundImage={backgroundImage}
                $transform={transform == 1}>
                {previewGenerating ? (
                    <>
                        <ImageCacheContainer $placeholderImg={placeholderImg}>
                            <ImageCachePlaceholder>
                                {generatingText}
                            </ImageCachePlaceholder>
                        </ImageCacheContainer>

                        {showSpinner ? (
                            <Icon
                                style={{
                                    position: 'absolute',
                                    zIndex: 5,
                                    top: 8,
                                    right: 8,
                                    width: 15,
                                }}
                                iconName={'tail-spin.svg'}
                            />
                        ) : null}
                    </>
                ) : null}
                <ImageLoader style={style} $display={!show} />
                <Image
                    ref={imageRef}
                    $show={show}
                    style={{...style, ...imageStyle}}
                    src={imageSource}
                    alt={alt ? alt : ''}
                    onLoad={imageLoadHandler}
                    onError={imageLoadErrorHandler}
                />
            </ImageContainer>
        </OuterContainer>
    );
};

const OuterContainer = styled.div`
    display: flex;
    width: inherit;
    height: inherit;
    justify-content: space-around;
    align-items: center;
    background: white;
    border-radius: 10px;
`;

const ImageContainer = styled.div<{
    $backgroundImage: string;
    $transform: boolean;
    $show?: boolean;
    $isHardware?: boolean;
}>`
    position: relative;
    overflow: hidden;
    width: ${({$show, $isHardware}) =>
        !$show || $isHardware ? 'inherit' : 'auto'};
    height: ${({$show, $isHardware}) =>
        !$show || $isHardware ? 'inherit' : 'auto'};

    &:before {
        display: ${({$show = false}) => ($show ? 'block' : 'none')};
        content: '';
        position: absolute;
        inset: 1px;
        border-radius: 20px;
        background: ${({$backgroundImage}) => $backgroundImage};
        transform: ${({$transform}) => ($transform ? 'rotate(90deg)' : '')};
    }
`;

const Image = styled.img<{$show: boolean}>`
    display: ${({$show = false}) => ($show ? 'block' : 'none')};
    position: relative;
    z-index: 2;
    width: 0;
    height: 0;
    object-fit: contain;
`;

const ImageLoader = styled(ImageWrapper)<{
    $display: boolean;
}>`
    border-radius: 10px;
    width: inherit;
    height: inherit;
    display: ${({$display}) => ($display ? 'block' : 'none')};
`;

const ImageCacheContainer = styled.div<{$placeholderImg?: string}>`
    display: flex;
    align-items: center;
    position: absolute;
    z-index: 5;
    padding: 10px;
    background: rgba(255, 255, 255, 0.7);
    height: 100%;
    width: 100%;
    border-radius: 8px;
    ${({$placeholderImg}) =>
        $placeholderImg
            ? `
        &:before {
            content: '';
            position: absolute;
            z-index: -1;
            opacity: 0.5;
            width: calc(100% - 20px);
            height: calc(100% - 20px);
            background: ${$placeholderImg};
            background-repeat: no-repeat;
            background-size: contain, 70px 70px;
            background-position: center;
        }
    `
            : null}
`;

const ImageCachePlaceholder = styled.div`
    text-align: center;
    font-weight: 600;
    font-size: 13px;
    color: #616161;
    flex-grow: 1;
    @media screen and (max-width: 768px) and (orientation: portrait),
        screen and (max-width: 1024px) and (orientation: landscape) {
        font-size: 8px;
    }
`;
